/**
 * The SAP connection — where the platform reads the journal from, and what
 * the last read found.
 *
 * Three things on one card, in the order somebody setting this up needs them:
 * the settings (address, how the platform signs in, which company codes,
 * when), two buttons that reach SAP (test, and read now), and the last read's
 * report — lines read, what was refused and why, which accounts no rule
 * covers. A journal file can be sent from here too; it lands where a live
 * read does.
 *
 * The secret is the one field that behaves differently, and the panel says
 * so: it is named (an environment secret the deployment holds) or typed once
 * and sent, and afterwards the platform answers only "set" or "not set". The
 * typed value is cleared from the page the moment it has been sent.
 *
 * Takes its source and site as props rather than from the session, so it can
 * be exercised against a platform that is not there (financeSystem tests).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DataSource } from "../../api/dataSource";
import { Button, Card, EmptyState, Field, Input, Select, Table, numClass } from "../../design/components";
import { REJECTION_LABEL } from "../../domain/sap";
import {
  FAILURE_LABEL,
  SCHEDULES,
  parseCompanyCodes,
  type FinanceConnection,
  type FinanceConnectionInput,
  type FinanceRun,
  type FinanceStatus,
  type FinanceTest,
  type SapAuthMode,
} from "../../domain/sapConnection";
import { locale, t, tn } from "../../i18n";
import s from "./FinanceSystem.module.css";

const DEFAULT_PATH = "/sap/opu/odata/sap/API_OPLACCTGDOCITEMCUBE_SRV/A_OperationalAcctgDocItemCube";

interface Draft {
  label: string;
  baseUrl: string;
  authMode: SapAuthMode;
  username: string;
  clientId: string;
  tokenUrl: string;
  scope: string;
  sapClient: string;
  companyCodes: string;
  servicePath: string;
  planPath: string;
  scheduleMinutes: number | null;
  enabled: boolean;
  secretMode: "keep" | "name" | "enter";
  secretRef: string;
  secret: string;
}

function draftOf(c: FinanceConnection | null): Draft {
  return {
    label: c?.label ?? "",
    baseUrl: c?.baseUrl ?? "",
    authMode: c?.authMode ?? "oauth_client_credentials",
    username: c?.username ?? "",
    clientId: c?.clientId ?? "",
    tokenUrl: c?.tokenUrl ?? "",
    scope: c?.scope ?? "",
    sapClient: c?.sapClient ?? "",
    companyCodes: (c?.companyCodes ?? []).join(", "),
    servicePath: c?.servicePath ?? DEFAULT_PATH,
    planPath: c?.planPath ?? "",
    scheduleMinutes: c?.scheduleMinutes ?? null,
    enabled: c?.enabled ?? false,
    secretMode: c?.secret.set || c?.secret.name ? "keep" : "name",
    secretRef: c?.secret.name ?? "",
    secret: "",
  };
}

function inputOf(d: Draft, c: FinanceConnection | null): FinanceConnectionInput {
  const oauth = d.authMode === "oauth_client_credentials";
  return {
    enabled: d.enabled,
    label: d.label.trim() || null,
    baseUrl: d.baseUrl.trim(),
    servicePath: d.servicePath.trim() || null,
    planPath: d.planPath.trim() || null,
    authMode: d.authMode,
    username: oauth ? null : d.username.trim() || null,
    clientId: oauth ? d.clientId.trim() || null : null,
    tokenUrl: oauth ? d.tokenUrl.trim() || null : null,
    scope: oauth ? d.scope.trim() || null : null,
    sapClient: d.sapClient.trim() || null,
    companyCodes: parseCompanyCodes(d.companyCodes),
    scheduleMinutes: d.scheduleMinutes,
    /* the site's own ranges are kept as they are; this panel does not edit them */
    accountRules: c?.accountRules,
    ...(d.secretMode === "name" && d.secretRef.trim() ? { secretRef: d.secretRef.trim() } : {}),
    ...(d.secretMode === "enter" && d.secret ? { secret: d.secret } : {}),
  };
}

function when(iso: string | null | undefined): string {
  if (!iso) return t("never");
  return new Date(iso).toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" });
}

function secretSays(c: FinanceConnection | null): string {
  if (!c || (!c.secret.set && !c.secret.name)) return t("No secret is set.");
  if (c.secret.source === "environment")
    return c.secret.set
      ? t("Set — read from the platform's environment as {name}.", { name: c.secret.name ?? "" })
      : t("Named {name}, but the platform's environment does not hold it yet.", { name: c.secret.name ?? "" });
  return c.secret.set
    ? t("Set — entered once and kept sealed on the platform.")
    : t("Entered once, but this platform can no longer open it; enter it again or name an environment secret.");
}

function failureSays(kind: string | null | undefined, fallback: string | null | undefined): string {
  if (kind && kind in FAILURE_LABEL) return t(FAILURE_LABEL[kind as keyof typeof FAILURE_LABEL]);
  return fallback ?? t("the read did not complete");
}

export function SapConnectionPanel({
  source,
  siteId,
  canConnect,
  canImport,
}: {
  source: DataSource;
  siteId: string;
  canConnect: boolean;
  canImport: boolean;
}) {
  const qc = useQueryClient();
  const demo = source.kind === "mock";
  const connection = useQuery({
    queryKey: ["finance-connection", siteId],
    queryFn: () => source.financeConnection(siteId),
    enabled: canConnect,
    retry: false,
  });
  const status = useQuery({
    queryKey: ["finance-status", siteId],
    queryFn: () => source.financeStatus(siteId),
    retry: false,
    /* while a read runs, ask again until it has finished */
    refetchInterval: (q) => ((q.state.data as FinanceStatus | undefined)?.running ? 2_000 : false),
  });
  /* The form shows the saved connection until somebody edits it; from the
     first keystroke it is the person's, and a refetch does not overwrite it. */
  const [edited, setDraft] = useState<Draft | null>(null);
  const draft = edited ?? draftOf(connection.data ?? null);
  const [test, setTest] = useState<FinanceTest | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const wasRunning = status.data?.running;
  useEffect(() => {
    /* a read that has just finished changes the figures above */
    if (wasRunning === false) void qc.invalidateQueries({ queryKey: ["finance-latest", siteId] });
  }, [wasRunning, qc, siteId]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({ ...draft, [k]: v });

  const save = useMutation({
    mutationFn: () => source.saveFinanceConnection(siteId, inputOf(draft, connection.data ?? null)),
    onSuccess: (saved) => {
      qc.setQueryData(["finance-connection", siteId], saved);
      void qc.invalidateQueries({ queryKey: ["finance-status", siteId] });
      /* the typed secret leaves the page the moment it has been sent */
      setDraft((d) => {
        const base = d ?? draftOf(saved);
        return { ...base, secret: "", secretMode: saved.secret.set || saved.secret.name ? "keep" : base.secretMode };
      });
      setNotice({ ok: true, text: t("Saved. The secret, if one was sent, is not shown again.") });
    },
    onError: (e) => setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  });
  const probe = useMutation({
    mutationFn: () => source.testFinanceConnection(siteId),
    onSuccess: (r) => setTest(r),
    onError: (e) => setTest({ ok: false, service: "", says: e instanceof Error ? e.message : String(e) }),
  });
  const readNow = useMutation({
    mutationFn: () => source.readFinanceNow(siteId),
    onSuccess: (r) => {
      setNotice({
        ok: true,
        text: t("Reading {year} period {period} from SAP. The report below updates when it finishes.", {
          year: r.fiscalYear,
          period: r.fiscalPeriod,
        }),
      });
      void qc.invalidateQueries({ queryKey: ["finance-status", siteId] });
    },
    onError: (e) => setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => source.importFinanceFile(siteId, file.name, await file.text()),
    onSuccess: (r) => {
      setNotice({
        ok: true,
        text:
          r.kind === "journal"
            ? t("The journal file was read: {n}. It is now the read shown above.", {
                n: tn(r.report.lines ?? 0, "{n} line", "{n} lines"),
              })
            : t("The management pack was recorded: {n} placed by its rules.", {
                n: tn(r.report.mapped ?? 0, "{n} line", "{n} lines"),
              }),
      });
      void qc.invalidateQueries({ queryKey: ["finance-status", siteId] });
      void qc.invalidateQueries({ queryKey: ["finance-latest", siteId] });
    },
    onError: (e) => setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  });

  const saved = connection.data ?? null;
  const oauth = draft.authMode === "oauth_client_credentials";
  const running = Boolean(status.data?.running) || readNow.isPending;

  return (
    <Card
      title="SAP connection"
      sub={t("Where the journal is read from; secrets are never shown again")}
      collapsible
      testId="sap-connection"
    >
      {demo && (
        <p className={s.warn}>
          {t(
            "The demo keeps these settings for this session only and never contacts SAP. A journal file sent below is read the same way the platform reads it.",
          )}
        </p>
      )}

      {!canConnect ? (
        <p className={s.note}>
          {t(
            "Only an administrator can set up the connection. Anyone who can see the finance system can see the last read below.",
          )}
        </p>
      ) : connection.isError ? (
        <p className={s.fail}>
          {connection.error instanceof Error ? connection.error.message : String(connection.error)}
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className={s.form}>
            <Field label="Name for this system">
              <Input
                value={draft.label}
                placeholder={t("S/4HANA · production")}
                onChange={(e) => set("label", e.target.value)}
              />
            </Field>
            <Field label="SAP address">
              <Input
                value={draft.baseUrl}
                placeholder="https://my000000-api.s4hana.cloud.sap"
                inputMode="url"
                onChange={(e) => set("baseUrl", e.target.value)}
              />
            </Field>
            <Field label="How the platform signs in">
              <Select value={draft.authMode} onChange={(e) => set("authMode", e.target.value as SapAuthMode)}>
                <option value="oauth_client_credentials">{t("OAuth 2.0 client credentials (S/4HANA Cloud)")}</option>
                <option value="basic">{t("User and password (communication or technical user)")}</option>
              </Select>
            </Field>
            {oauth ? (
              <>
                <Field label="Client id">
                  <Input value={draft.clientId} onChange={(e) => set("clientId", e.target.value)} />
                </Field>
                <Field label="Token URL">
                  <Input
                    value={draft.tokenUrl}
                    placeholder="https://my000000-api.s4hana.cloud.sap/sap/bc/sec/oauth2/token"
                    onChange={(e) => set("tokenUrl", e.target.value)}
                  />
                </Field>
                <Field label="Scope (if SAP asks for one)">
                  <Input value={draft.scope} onChange={(e) => set("scope", e.target.value)} />
                </Field>
              </>
            ) : (
              <Field label="User">
                <Input value={draft.username} autoComplete="off" onChange={(e) => set("username", e.target.value)} />
              </Field>
            )}
            <Field label="SAP client (on-premise)">
              <Input
                value={draft.sapClient}
                placeholder="100"
                inputMode="numeric"
                maxLength={3}
                onChange={(e) => set("sapClient", e.target.value)}
              />
            </Field>
            <Field label="Company codes">
              <Input
                value={draft.companyCodes}
                placeholder="1710, 1720"
                onChange={(e) => set("companyCodes", e.target.value)}
              />
            </Field>
            <Field label="Read on a schedule">
              <Select
                value={draft.scheduleMinutes === null ? "" : String(draft.scheduleMinutes)}
                onChange={(e) => set("scheduleMinutes", e.target.value === "" ? null : Number(e.target.value))}
              >
                {SCHEDULES.map((o) => (
                  <option key={o.label} value={o.minutes === null ? "" : String(o.minutes)}>
                    {t(o.label)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Service path">
              <Input value={draft.servicePath} onChange={(e) => set("servicePath", e.target.value)} />
            </Field>
            <Field label="Plan entity path (optional)">
              <Input
                value={draft.planPath}
                placeholder={t("none — actuals only")}
                onChange={(e) => set("planPath", e.target.value)}
              />
            </Field>

            <div className={`${s.wide} ${s.secret}`}>
              <p className={s.note} data-testid="sap-secret-state">
                <strong>{t("Password or client secret:")}</strong> {secretSays(saved)}
              </p>
              <div className={s.actions}>
                {(saved?.secret.set || saved?.secret.name) && (
                  <label className={s.check}>
                    <input
                      type="radio"
                      name="secret-mode"
                      checked={draft.secretMode === "keep"}
                      onChange={() => set("secretMode", "keep")}
                    />
                    {t("keep it as it is")}
                  </label>
                )}
                <label className={s.check}>
                  <input
                    type="radio"
                    name="secret-mode"
                    checked={draft.secretMode === "name"}
                    onChange={() => set("secretMode", "name")}
                  />
                  {t("name a secret the platform holds")}
                </label>
                <label className={s.check}>
                  <input
                    type="radio"
                    name="secret-mode"
                    checked={draft.secretMode === "enter"}
                    onChange={() => set("secretMode", "enter")}
                  />
                  {t("enter it once")}
                </label>
              </div>
              {draft.secretMode === "name" && (
                <Field label="Environment secret name">
                  <Input
                    value={draft.secretRef}
                    placeholder="SLT_FINANCE_SECRET_MADINAH"
                    onChange={(e) => set("secretRef", e.target.value.toUpperCase())}
                  />
                </Field>
              )}
              {draft.secretMode === "enter" &&
                (saved && !saved.canStoreSecret ? (
                  <p className={s.warn}>
                    {t(
                      "This platform can't store a typed secret. Name an environment secret instead; your administrator adds it to Secret Manager.",
                    )}
                  </p>
                ) : (
                  <Field label="Secret — sent once, never shown again">
                    <Input
                      type="password"
                      autoComplete="new-password"
                      value={draft.secret}
                      onChange={(e) => set("secret", e.target.value)}
                    />
                  </Field>
                ))}
            </div>

            <label className={`${s.check} ${s.wide}`}>
              <input type="checkbox" checked={draft.enabled} onChange={(e) => set("enabled", e.target.checked)} />
              {t("Enabled — the schedule may read from SAP")}
            </label>
          </div>
          <div className={s.actions}>
            <Button variant="primary" type="submit" disabled={save.isPending || !draft.baseUrl.trim()}>
              {t("Save the connection")}
            </Button>
            <Button onClick={() => probe.mutate()} disabled={!saved || probe.isPending}>
              {probe.isPending ? t("Testing…") : t("Test the connection")}
            </Button>
            <Button onClick={() => readNow.mutate()} disabled={!saved || running}>
              {running ? t("Reading…") : t("Read now")}
            </Button>
          </div>
          {test && (
            <p className={test.ok ? s.ok : s.fail} data-testid="sap-test-result">
              {test.says ? t(test.says) : failureSays(test.kind, null)}
              {!test.ok && test.error && <span className={s.why}> — {test.error}</span>}
            </p>
          )}
        </form>
      )}

      {notice && <p className={notice.ok ? s.ok : s.fail}>{notice.text}</p>}

      <LastRun status={status.data} loading={status.isPending} failed={status.isError} />

      {canImport && (
        <div className={s.actions}>
          <label className={s.check}>
            <span>{t("Send a journal file (CSV in SAP's field names) or the monthly pack:")}</span>
            <input
              type="file"
              accept=".csv,.txt,text/csv"
              aria-label={t("Journal or management pack file")}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      )}
    </Card>
  );
}

function LastRun({ status, loading, failed }: { status?: FinanceStatus; loading: boolean; failed: boolean }) {
  if (loading) return null;
  if (failed || !status) return <p className={s.note}>{t("The platform did not say how the last read went.")}</p>;
  const run: FinanceRun | null = status.lastRun;
  if (!run) return <EmptyState>{t("Nothing has been read yet.")}</EmptyState>;
  const report = run.report ?? {};
  const rejected = report.rejected ?? [];
  const unmapped = report.unmapped ?? [];
  const currency = report.currency ?? "";
  return (
    <div data-testid="sap-last-run">
      <h3 className={s.h3}>{t("The last read")}</h3>
      <p className={run.status === "failed" ? s.fail : s.says}>
        {run.status === "running"
          ? t("Reading since {when}.", { when: when(run.startedAt) })
          : run.status === "ok"
            ? t("{source} on {when}: {n} read, {mapped} placed.", {
                source: run.source === "file" ? t("From the file {name}", { name: run.fileName ?? "" }) : t("From SAP"),
                when: when(run.finishedAt),
                n: tn(report.lines ?? 0, "{n} line", "{n} lines"),
                mapped: report.mapped ?? 0,
              })
            : t("Failed on {when}: {why}", {
                when: when(run.finishedAt),
                why: failureSays(run.errorKind, run.says ?? run.error),
              })}
      </p>
      {run.status === "failed" && run.error && <p className={s.why}>{run.error}</p>}
      {run.status === "ok" && (
        <>
          {rejected.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th>{t("Not counted, and why")}</th>
                  <th className={numClass}>{t("Lines")}</th>
                  <th className={numClass}>{t("Amount")}</th>
                </tr>
              </thead>
              <tbody>
                {rejected.map((r) => (
                  <tr key={r.reason}>
                    <td>{t(REJECTION_LABEL[r.reason] ?? r.reason)}</td>
                    <td className={numClass}>{r.lines}</td>
                    <td className={numClass}>
                      {Math.round(r.amount).toLocaleString(locale())} {currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {unmapped.length > 0 && (
            <p className={s.warn}>
              {t("Accounts no rule covers: {list}.", {
                list: unmapped
                  .map((u) => `${u.account} (${Math.round(Math.abs(u.amount)).toLocaleString(locale())})`)
                  .join(", "),
              })}
            </p>
          )}
          {rejected.length === 0 && unmapped.length === 0 && (
            <p className={s.note}>{t("Every line was counted, and every account had a rule.")}</p>
          )}
        </>
      )}
      {status.scheduleMinutes && status.enabled ? (
        <p className={s.note}>
          {t("Reads on its own every {n} minutes; the last successful read was {when}.", {
            n: status.scheduleMinutes,
            when: when(status.lastSuccessAt),
          })}
        </p>
      ) : null}
    </div>
  );
}
